import { v4 as uuidv4 } from 'uuid';

class SessionManager {
    constructor() {
        this.sessionId = null;
        this.channel = null;
        this.onSessionTakenCallback = null;
        this.checkInterval = null;
    }

    generateSessionId() {
        return uuidv4();
    }

    init(onSessionTaken) {
        console.log('[SessionManager] Initializing session manager');
        this.onSessionTakenCallback = onSessionTaken;
        
        // Generate or get session ID
        this.sessionId = this.generateSessionId();
        console.log('[SessionManager] Generated session ID:', this.sessionId);
        
        // Store session ID in sessionStorage (tab-specific)
        sessionStorage.setItem('activeSessionId', this.sessionId);
        
        // Initialize BroadcastChannel for cross-tab communication
        if ('BroadcastChannel' in window) {
            this.channel = new BroadcastChannel('auth_session');
            
            this.channel.onmessage = (event) => {
                console.log('[SessionManager] Main channel received message:', event.data);
                
                if (event.data.type === 'NEW_SESSION' && event.data.sessionId !== this.sessionId) {
                    // Another tab is taking over
                    console.log('[SessionManager] Another session is taking over');
                    this.handleSessionTaken();
                }
                
                if (event.data.type === 'SESSION_CHECK') {
                    // Respond that this tab is active
                    console.log('[SessionManager] Responding to session check');
                    this.channel.postMessage({
                        type: 'SESSION_ACTIVE',
                        sessionId: this.sessionId,
                        timestamp: Date.now()
                    });
                }
            };
            
            // Announce this session
            this.announceSession();
        } else {
            // Fallback for browsers without BroadcastChannel
            this.initStorageFallback();
        }
        
        // Check for other sessions periodically
        this.startSessionCheck();
    }

    announceSession() {
        if (this.channel) {
            this.channel.postMessage({
                type: 'NEW_SESSION',
                sessionId: this.sessionId,
                timestamp: Date.now()
            });
        } else {
            // Fallback: use localStorage
            localStorage.setItem('currentSessionId', this.sessionId);
            localStorage.setItem('sessionTimestamp', Date.now().toString());
        }
    }

    initStorageFallback() {
        // Listen for storage events (works across tabs)
        window.addEventListener('storage', (e) => {
            if (e.key === 'currentSessionId' && e.newValue !== this.sessionId) {
                this.handleSessionTaken();
            }
        });
    }

    startSessionCheck() {
        // Check every 2 seconds if we're still the active session
        this.checkInterval = setInterval(() => {
            if ('BroadcastChannel' in window) {
                // With BroadcastChannel, we rely on messages
                return;
            }
            
            // Fallback: check localStorage
            const storedSessionId = localStorage.getItem('currentSessionId');
            const storedTimestamp = parseInt(localStorage.getItem('sessionTimestamp') || '0');
            const now = Date.now();
            
            // If another session is newer and different, we should logout
            if (storedSessionId !== this.sessionId && (now - storedTimestamp) < 5000) {
                this.handleSessionTaken();
            }
        }, 2000);
    }

    handleSessionTaken() {
        // Clear interval
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
        }
        
        // Close channel
        if (this.channel) {
            this.channel.close();
        }
        
        // Clear session storage
        sessionStorage.removeItem('activeSessionId');
        
        // Trigger callback (logout)
        if (this.onSessionTakenCallback) {
            this.onSessionTakenCallback();
        }
    }

    checkForExistingSessions() {
        console.log('[SessionManager] Checking for existing sessions');
        return new Promise((resolve) => {
            let hasActiveSessions = false;
            
            if ('BroadcastChannel' in window) {
                const tempChannel = new BroadcastChannel('auth_session');
                
                tempChannel.onmessage = (event) => {
                    console.log('[SessionManager] Received message:', event.data);
                    if (event.data.type === 'SESSION_ACTIVE') {
                        hasActiveSessions = true;
                        console.log('[SessionManager] Active session detected');
                    }
                };
                
                // Ask if any sessions are active
                tempChannel.postMessage({ type: 'SESSION_CHECK' });
                
                // Wait for responses
                setTimeout(() => {
                    tempChannel.close();
                    console.log('[SessionManager] Check complete, hasActiveSessions:', hasActiveSessions);
                    resolve(hasActiveSessions);
                }, 500);
            } else {
                // Fallback: check localStorage
                const storedSessionId = localStorage.getItem('currentSessionId');
                const storedTimestamp = parseInt(localStorage.getItem('sessionTimestamp') || '0');
                const now = Date.now();
                
                // Consider session active if updated within last 5 seconds
                hasActiveSessions = storedSessionId && (now - storedTimestamp) < 5000;
                resolve(hasActiveSessions);
            }
        });
    }

    destroy() {
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
        }
        
        if (this.channel) {
            this.channel.close();
        }
        
        sessionStorage.removeItem('activeSessionId');
    }
}

const sessionManagerInstance = new SessionManager();
export default sessionManagerInstance;